import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { addDays, eatDayOf, formatIsoDay, INFECTION_TEXT_LIMITS } from '@wodi/domain';
import type { InfectionLineListView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  infectionKeys,
  infectionOptionsQuery,
  infectionStaysQuery,
  lineListQuery,
  recordInfectionEvent,
} from '../api/infection.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import {
  dateTimeText,
  dayText,
  errorText,
  LoadFailed,
  NotAllowed,
  OfflineListNotice,
} from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';

// Infection control (docs/plans/phase3-operations-modules.md §2.4): the line list of
// hospital-acquired infections with counts by category and ward, the precautions in place,
// and recording an infection on a current stay. A patient the reader may not see under the
// sensitivity rule is listed as "Restricted record". Opening the list is audited with counts.

const RESTRICTED = 'Restricted record';

function patientCell(p: InfectionLineListView['events'][number]['patient']) {
  if (p === null) return RESTRICTED;
  return (
    <>
      <Link to="/patients/$patientId" params={{ patientId: p.id }}>
        {p.displayName}
      </Link>{' '}
      <span className="wodi-id">{p.patientNumber}</span>
    </>
  );
}

function RecordDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const stays = useQuery(infectionStaysQuery);
  const options = useQuery(infectionOptionsQuery);
  const today = formatIsoDay(eatDayOf(new Date()));
  const [admissionId, setAdmissionId] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [onset, setOnset] = useState<string | null>(today);
  const [organism, setOrganism] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const record = useMutation({
    mutationFn: () =>
      recordInfectionEvent(admissionId ?? '', {
        categoryCode: category ?? '',
        onsetOn: onset ?? today,
        organism: organism.trim() === '' ? null : organism.trim(),
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: infectionKeys.all });
      toasts.show({ title: 'Infection recorded' });
      onClose();
    },
  });
  const ready = admissionId !== null && category !== null && onset !== null;
  return (
    <Dialog
      title="Record an infection"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {stays.isError || options.isError ? (
          <Notice tone="error">The form couldn&apos;t be loaded. Close it and try again.</Notice>
        ) : null}
        <Select
          label="Stay"
          placeholder={stays.isPending ? 'Loading…' : 'Choose the patient'}
          description="Patients admitted here now."
          options={(stays.data?.items ?? []).map((s) => ({
            id: s.admissionId,
            label: `${s.patient === null ? RESTRICTED : `${s.patient.displayName} (${s.patient.patientNumber})`}, ${s.wardName ?? 'no ward yet'}${s.bedLabel === null ? '' : `, bed ${s.bedLabel}`}`,
          }))}
          value={admissionId}
          onChange={(key) => {
            setAdmissionId(key === null ? null : String(key));
          }}
          {...(tried && admissionId === null ? { errorMessage: 'Choose the stay.' } : {})}
        />
        <Select
          label="Category"
          placeholder="Choose one"
          options={(options.data?.categories ?? [])
            .filter((c) => c.active)
            .map((c) => ({ id: c.code, label: c.display }))}
          value={category}
          onChange={(key) => {
            setCategory(key === null ? null : String(key));
          }}
          {...(tried && category === null ? { errorMessage: 'Choose a category.' } : {})}
        />
        <DateField
          label="Onset"
          description="The day signs began."
          value={onset}
          onChange={setOnset}
          maxValue={today}
          {...(tried && onset === null ? { errorMessage: 'Enter the onset day.' } : {})}
        />
        <TextField
          label="Organism (optional)"
          value={organism}
          onChange={setOrganism}
          maxLength={INFECTION_TEXT_LIMITS.organism}
        />
        <TextArea
          label="Note (optional)"
          value={note}
          onChange={setNote}
          maxLength={INFECTION_TEXT_LIMITS.note}
        />
        {record.isError ? (
          <Notice tone="error">{errorText(record.error, "It wasn't saved. Try again.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isPending={record.isPending}
          pendingLabel="Recording…"
          onPress={() => {
            setTried(true);
            if (ready) record.mutate();
          }}
        >
          Record infection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Counts({ list }: { list: InfectionLineListView }) {
  const table = (label: string, rows: InfectionLineListView['counts']['byCategory']) => (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>{label === 'Counts by category' ? 'Category' : 'Ward'}</Column>
          <Column>Infections</Column>
        </TableHeader>
        <TableBody
          items={rows.map((r) => ({ ...r, id: r.key === '' ? 'none' : r.key }))}
          renderEmptyState={() => 'None in this range.'}
        >
          {(r) => (
            <Row id={r.id}>
              <Cell>{r.label}</Cell>
              <Cell>
                <span className={styles.numeric}>{String(r.count)}</span>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
  return (
    <div className={styles.grid}>
      <Section title="By category" headingLevel={3}>
        {table('Counts by category', list.counts.byCategory)}
      </Section>
      <Section title="By ward" headingLevel={3}>
        {table('Counts by ward', list.counts.byWard)}
      </Section>
    </div>
  );
}

function LineList({ list }: { list: InfectionLineListView }) {
  return (
    <TableScroll>
      <Table flat aria-label="Line list">
        <TableHeader>
          <Column isRowHeader>Patient</Column>
          <Column>Category</Column>
          <Column>Onset</Column>
          <Column>Days after admission</Column>
          <Column>Ward</Column>
          <Column>Organism</Column>
          <Column>Recorded</Column>
        </TableHeader>
        <TableBody
          items={list.events}
          renderEmptyState={() => 'No infections with onset in this range.'}
        >
          {(e) => (
            <Row id={e.id}>
              <Cell>{patientCell(e.patient)}</Cell>
              <Cell>{e.category.display}</Cell>
              <Cell>{dayText(e.onsetOn)}</Cell>
              <Cell>
                <span className={styles.numeric}>{String(e.daysFromAdmission)}</span>
              </Cell>
              <Cell>{e.wardName ?? 'Not recorded'}</Cell>
              <Cell>{e.organism ?? 'Not recorded'}</Cell>
              <Cell>
                {dateTimeText(e.recordedAt)}, {e.recordedBy.displayName}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Flags({ list }: { list: InfectionLineListView }) {
  return (
    <TableScroll>
      <Table flat aria-label="Precautions in place">
        <TableHeader>
          <Column isRowHeader>Patient</Column>
          <Column>Precaution</Column>
          <Column>Since</Column>
          <Column>Ward</Column>
        </TableHeader>
        <TableBody items={list.flags} renderEmptyState={() => 'No precautions in place.'}>
          {(f) => (
            <Row id={f.id}>
              <Cell>{patientCell(f.patient)}</Cell>
              <Cell>{f.words}</Cell>
              <Cell>{dateTimeText(f.startedAt)}</Cell>
              <Cell>{f.wardName ?? 'Not admitted'}</Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

export function InfectionControlPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [now] = useState(() => new Date());
  const today = formatIsoDay(eatDayOf(now));
  const [from, setFrom] = useState<string | null>(formatIsoDay(addDays(eatDayOf(now), -30)));
  const [to, setTo] = useState<string | null>(today);
  const [recording, setRecording] = useState(false);
  const allowed = session !== null && can(session, 'infection.read');
  const range = { from: from ?? today, to: to ?? today };
  const list = useQuery({ ...lineListQuery(range.from, range.to), enabled: allowed });
  if (!allowed) return <NotAllowed title="Infection control" what="see the infection line list" />;
  const mayRecord = can(session, 'infection.record');
  return (
    <div className={styles.page}>
      <PageHeader
        title="Infection control"
        description="Hospital-acquired infections by onset, and the precautions in place now."
        {...(mayRecord
          ? {
              actions: (
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    setRecording(true);
                  }}
                >
                  Record an infection
                </Button>
              ),
            }
          : {})}
      />
      <OfflineListNotice online={online} />
      <Section title="Line list" description="Infections whose onset falls in the range.">
        <div className={styles.form}>
          <div className={styles.row}>
            <DateField label="From" value={from} onChange={setFrom} maxValue={today} />
            <DateField label="To" value={to} onChange={setTo} maxValue={today} />
          </div>
          {list.isPending ? <LoadingBlock label="Loading the line list" rows={5} /> : null}
          {list.isError ? (
            <LoadFailed
              error={list.error}
              fallback="The line list couldn't be loaded. Try again."
              onRetry={() => void list.refetch()}
            />
          ) : null}
          {list.isSuccess ? (
            <>
              <p className={styles.meta}>
                {`${String(list.data.counts.total)} ${list.data.counts.total === 1 ? 'infection' : 'infections'} from ${dayText(list.data.from)} to ${dayText(list.data.to)}.`}
              </p>
              <LineList list={list.data} />
            </>
          ) : null}
        </div>
      </Section>
      {list.isSuccess ? (
        <>
          <Counts list={list.data} />
          <Section title="Precautions in place" description="Flags showing on banners now.">
            <Flags list={list.data} />
          </Section>
        </>
      ) : null}
      <p className={styles.meta}>
        Categories and precautions are set in Settings, Operations lists. Wodi doesn&apos;t apply
        surveillance definitions; the infection-control nurse decides what counts.
      </p>
      {recording ? (
        <RecordDialog
          onClose={() => {
            setRecording(false);
          }}
        />
      ) : null}
    </div>
  );
}
